import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSTextField from "views/system-design/DSTextField";
import DSFormControlLabel from "views/system-design/DSFormControlLabel";
import DSCheckbox from "views/system-design/DSCheckBox";
import DSButton from "views/system-design/DSButton";
import DSPrimaryButton from "views/system-design/DSPrimaryButton";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSBox from "views/system-design/DSBox";
import type { TesterStory, StoryFormData } from "../types";

interface StoryFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: StoryFormData) => void;
  saving: boolean;
  initialData?: TesterStory | null;
}

const StoryFormDialog: React.FC<StoryFormDialogProps> = ({
  open,
  onClose,
  onSave,
  saving,
  initialData,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [prompt, setPrompt] = useState("");
  const [maxSteps, setMaxSteps] = useState(15);
  const [timeoutMs, setTimeoutMs] = useState(60000);
  const [requiresInternet, setRequiresInternet] = useState(false);

  // Reset form when initialData or open changes
  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDescription(initialData.description);
      setPrompt(initialData.prompt);
      setMaxSteps(initialData.maxSteps);
      setTimeoutMs(initialData.timeoutMs);
      setRequiresInternet(initialData.requiresInternet);
    } else {
      setName("");
      setDescription("");
      setPrompt("");
      setMaxSteps(15);
      setTimeoutMs(60000);
      setRequiresInternet(false);
    }
  }, [initialData, open]);

  const handleSave = () => {
    onSave({
      name,
      description,
      prompt,
      maxSteps,
      timeoutMs,
      requiresInternet,
    });
  };

  const isFormValid =
    name.trim() !== "" && description.trim() !== "" && prompt.trim() !== "";

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DSDialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {initialData ? "Edit Story" : "Create Story"}
        <CloseIcon sx={{ cursor: "pointer" }} onClick={onClose} />
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <DSTextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <DSTextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            fullWidth
            multiline
            rows={2}
          />
          <DSTextField
            label="Prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            required
            fullWidth
            multiline
            rows={6}
            sx={{
              "& textarea": {
                fontFamily: "monospace",
                fontSize: "0.85rem",
              },
            }}
          />
          <DSTextField
            label="Max Steps"
            type="number"
            value={maxSteps}
            onChange={(e) => setMaxSteps(Number(e.target.value))}
            required
            fullWidth
          />
          <DSTextField
            label="Timeout (ms)"
            type="number"
            value={timeoutMs}
            onChange={(e) => setTimeoutMs(Number(e.target.value))}
            required
            fullWidth
          />
          <DSFormControlLabel
            control={
              <DSCheckbox
                checked={requiresInternet}
                onChange={(e) => setRequiresInternet(e.target.checked)}
              />
            }
            label="Requires Internet"
          />
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSButton type="button" variant="text" onClick={onClose}>
          Cancel
        </DSButton>
        <DSPrimaryButton onClick={handleSave} disabled={saving || !isFormValid}>
          {saving ? <DSCircularProgress size={20} /> : "Save"}
        </DSPrimaryButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default StoryFormDialog;
